import React, { useState, useEffect } from 'react';
import { Compass } from 'lucide-react';
import CompassIndicator from './CompassIndicator';

interface OrientationModalProps {
  isOpen: boolean;
  onOrientationSubmit: (orientation: number) => void;
  onSkip: () => void;
  title: string;
  description: string;
  allowSkip?: boolean;
  initialOrientation?: number;
}

const OrientationModal: React.FC<OrientationModalProps> = ({
  isOpen,
  onOrientationSubmit,
  onSkip,
  title,
  description,
  allowSkip = true,
  initialOrientation = 0
}) => {
  const [orientation, setOrientation] = useState(initialOrientation);

  useEffect(() => {
    setOrientation(initialOrientation);
  }, [initialOrientation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onOrientationSubmit(orientation);
  };

  const handleSkip = () => {
    setOrientation(0);
    onSkip();
  };

  const handleOrientationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setOrientation(0);
      return;
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      // Normalize to 0-360 range
      const normalizedValue = ((numValue % 360) + 360) % 360;
      setOrientation(normalizedValue);
    }
  };

  // Get cardinal direction helper
  const getCardinalDirection = (degrees: number) => {
    const normalized = ((degrees % 360) + 360) % 360;
    if (normalized >= 337.5 || normalized < 22.5) return 'North';
    if (normalized >= 22.5 && normalized < 67.5) return 'Northeast';
    if (normalized >= 67.5 && normalized < 112.5) return 'East';
    if (normalized >= 112.5 && normalized < 157.5) return 'Southeast';
    if (normalized >= 157.5 && normalized < 202.5) return 'South';
    if (normalized >= 202.5 && normalized < 247.5) return 'Southwest';
    if (normalized >= 247.5 && normalized < 292.5) return 'West';
    if (normalized >= 292.5 && normalized < 337.5) return 'Northwest';
    return 'North';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center space-x-2 mb-4">
            <div className="p-1 bg-blue-100 rounded">
              <Compass size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600">{description}</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="orientation" className="block text-xs font-medium text-gray-700 mb-1">
                Front Door Orientation
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    id="orientation"
                    type="number"
                    value={orientation}
                    onChange={handleOrientationChange}
                    min="0"
                    max="360"
                    step="1"
                    placeholder="0"
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="text-xs font-medium text-gray-600 min-w-[60px]">
                    {getCardinalDirection(orientation)}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { degrees: 0, label: 'N' },
                    { degrees: 90, label: 'E' },
                    { degrees: 180, label: 'S' },
                    { degrees: 270, label: 'W' }
                  ].map(({ degrees, label }) => (
                    <button
                      key={degrees}
                      type="button"
                      onClick={() => setOrientation(degrees)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        orientation === degrees
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {label} {degrees}°
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Compact Compass Display */}
            <div className="flex justify-center py-2">
              <CompassIndicator orientation={orientation} size={80} showDegrees={true} />
            </div>

            {/* Action buttons */}
            <div className="flex space-x-2 pt-2">
              {allowSkip && (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500"
                >
                  Skip
                </button>
              )}
              <button
                type="submit"
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                Set Orientation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrientationModal; 